import { ArrowLeft } from 'lucide-react'
import { Link, Outlet, useLocation, useParams } from 'react-router-dom'

import type { RecordGroupDetail } from '@/types/api/record-group'

import { GroupSidebar } from './GroupSidebar'
import { RecordGroupBreadcrumbs } from './RecordGroupBreadcrumbs'
import { RecordGroupSectionNav } from './RecordGroupSectionNav'

interface RecordGroupDetailLayoutProps {
  group: RecordGroupDetail | null
}

export function RecordGroupDetailLayout({ group }: RecordGroupDetailLayoutProps) {
  const { recordGroupId } = useParams<{ recordGroupId: string }>()
  const isFormDesigner = useLocation().pathname.endsWith('/form')

  return (
    <section className="-m-4 flex h-[calc(100%+2rem)] min-h-0 w-auto min-w-0 flex-col overflow-hidden bg-background lg:-m-6 lg:h-[calc(100%+3rem)]">
      <div className={`${isFormDesigner ? 'hidden md:flex' : 'flex'} h-11 shrink-0 items-center overflow-x-auto md:h-16`}>
        {recordGroupId && (
          <RecordGroupBreadcrumbs
            recordGroupId={recordGroupId}
            className="flex h-7 items-center pl-4 md:pl-6"
          />
        )}
      </div>
      {isFormDesigner && recordGroupId && (
        <div className="flex h-11 shrink-0 items-center border-b border-border px-4 md:hidden">
          <Link
            to={`/record-groups/${recordGroupId}`}
            className="inline-flex min-w-0 items-center gap-2 text-sm font-medium text-foreground"
            aria-label={`Back to ${group?.name ?? 'record group'}`}
          >
            <ArrowLeft aria-hidden="true" className="h-4 w-4 shrink-0" />
            <span className="truncate">Back to {group?.name ?? 'record group'}</span>
          </Link>
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {/* The designer needs palette + canvas side by side, so the group tree only shows on wide screens there */}
        <div className={isFormDesigner ? 'hidden xl:contents' : 'contents'}>
          <GroupSidebar group={group} />
        </div>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <RecordGroupSectionNav group={group} hideOnMobile={isFormDesigner} />
          <main className={isFormDesigner ? "min-h-0 min-w-0 flex-1 overflow-hidden p-0" : "min-h-0 min-w-0 flex-1 overflow-y-auto p-4 lg:p-6"}>
            <Outlet />
          </main>
        </div>
      </div>
    </section>
  )
}
